"use client";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { useSignupStore } from "@/lib/store/use-signup-store";
import { nameRegex } from "@/lib/regex";

const PARENT_INFO_INPUT_TITLE = `보호자 정보를 입력해 주세요.
본인인증 시 입력 정보와 대조됩니다.`;

const PLACEHOLDER = {
  NAME: "보호자 이름을 입력해 주세요.",
  BIRTH_DATE: "생년월일 8자리를 입력해 주세요. (예: 19900101)",
};

const ERROR_MESSAGE = {
  NAME: "이름은 한글 2자 이상 10자 이하로 입력해주세요.",
  BIRTH_DATE: "생년월일은 8자리 숫자여야 합니다. (예: 19900101)",
};

// 생년월일 8자리 검증 (YYYYMMDD)
const birthDateRegex = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;

const parentInfoSchema = z.object({
  parentInputName: z.string().regex(nameRegex, ERROR_MESSAGE.NAME),
  parentInputBirth: z.string().regex(birthDateRegex, ERROR_MESSAGE.BIRTH_DATE),
});

type ParentInfoFormData = z.infer<typeof parentInfoSchema>;

export function ParentInfoInputStep() {
  const { parentInputName, parentInputBirth, setParentInfo, goToNextStep, goToPrevStep } =
    useSignupStore();

  const form = useForm<ParentInfoFormData>({
    resolver: zodResolver(parentInfoSchema),
    mode: "onBlur",
    defaultValues: {
      parentInputName: parentInputName || "",
      parentInputBirth: parentInputBirth || "",
    },
  });

  const { isValid, isDirty } = form.formState;

  const onSubmit = (data: ParentInfoFormData) => {
    setParentInfo({
      parentInputName: data.parentInputName,
      parentInputBirth: data.parentInputBirth,
    });
    goToNextStep();
  };

  return (
    <FixedLayout
      title={PARENT_INFO_INPUT_TITLE}
      formId="parent-info-form"
      buttonText="본인 인증하기"
      buttonDisabled={!isValid || (!isDirty && !parentInputName)}
      onBack={goToPrevStep}
    >
      <form
        id="parent-info-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full"
      >
        <div className="flex flex-col gap-6">
          {/* 보호자 이름 */}
          <Controller
            name="parentInputName"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="parent-info-name">보호자 이름</FieldLabel>
                <Input
                  {...field}
                  id="parent-info-name"
                  placeholder={PLACEHOLDER.NAME}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="next"
                  autoComplete="name"
                  rightIcon={
                    field.value.length > 0 ? (
                      <X
                        onClick={() => field.onChange("")}
                        className="size-4 text-muted-foreground cursor-pointer"
                      />
                    ) : null
                  }
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 보호자 생년월일 */}
          <Controller
            name="parentInputBirth"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="parent-info-birth">
                  보호자 생년월일
                </FieldLabel>
                <Input
                  {...field}
                  id="parent-info-birth"
                  placeholder={PLACEHOLDER.BIRTH_DATE}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="done"
                  inputMode="numeric"
                  maxLength={8}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    field.onChange(value);
                  }}
                  rightIcon={
                    field.value.length > 0 ? (
                      <X
                        onClick={() => field.onChange("")}
                        className="size-4 text-muted-foreground cursor-pointer"
                      />
                    ) : null
                  }
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        </div>
      </form>
    </FixedLayout>
  );
}
